import { AfterViewInit, Component, Input, OnInit } from '@angular/core';
import { ValidatorFn } from '@angular/forms';
import { ErrorMessages, FormBase, MakeUpCreditMode, MakeupCreditManager, Optional, Required, Validators, number } from '@sb/library/base';
import { BillingProfileDefaultMode, BillingProfileDisplayItem, EventCategoryDisplayItem, TeacherDisplayItem } from '@sb/library/data-models';
import { FormFieldSubformBaseComponent } from '../../form-field-subform-base.component';

class BillingProfileForm extends FormBase {
    EventCategory = Required(EventCategoryDisplayItem);
    DefaultLessonLength = Required(number);
    DefaultBillingMode = Required(BillingProfileDefaultMode);
    LessonPrice = Required(number);
    Credits = Optional(number);
}

@Component({
    selector: 'lib-input-billing-profile',
    templateUrl: './form-field-billing-profile.component.html'
})
export class FormFieldBillingProfileComponent extends FormFieldSubformBaseComponent<BillingProfileDisplayItem | null, BillingProfileForm> implements AfterViewInit, OnInit {

    private _categories!: EventCategoryDisplayItem[];
    private _teacher!: TeacherDisplayItem;
    private _showBanner: boolean = false;
    @Input()
    set categories(value: EventCategoryDisplayItem[]) {
        // when passed a valid category array this will always be larger than zero
        if (value && value.length > 0) {
            this.form.isLoading = false;
            this._categories = value;
            this.CategoriesAndTeachersReady();
        }
    }
    @Input()
    set teacher(value: TeacherDisplayItem) {
        if (value) {
            this._teacher = value;
            this.CategoriesAndTeachersReady();
        }
    }
    @Input() makeUpCreditMode!: MakeUpCreditMode;

    private _packagesMode: boolean = false;
    @Input()
    set packagesMode(value: boolean) {
        this._packagesMode = value;
        // these billing profile modes are not compatible with packages
        if (value && this.form.controls.DefaultBillingMode.value && !this.IsCurrentBillingProfileModePackagesCompatible()) {
            this.form.controls.DefaultBillingMode.setValue(BillingProfileDefaultMode.PerLesson);
            this.BillingModeChanged(BillingProfileDefaultMode.PerLesson);
        }
    }

    private _billingProfileToEdit?: BillingProfileDisplayItem;

    form = BillingProfileForm.Create(false);

    defaultLessonLengthErrorMessages: ErrorMessages = {
        required: _ => {
            return $localize`:@@LibraryDefaultLessonLengthRequiredErrorMessage:Please enter a valid duration`
        }
    }

    lessonPricePerLessonErrorMessages: ErrorMessages = {
        required: _ => {
            return $localize`:@@LibraryLessonPriceRequiredErrorMessage:Please enter a value`;
        }
    }

    lessonPriceHourlyErrorMessages: ErrorMessages = {
        required: _ => {
            return $localize`:@@LibraryHourlyPriceRequiredErrorMessage:Please enter a value`;
        }
    }

    lessonPriceMonthlyErrorMessages: ErrorMessages = {
        required: _ => {
            return $localize`:@@LibraryMonthlyPriceRequiredErrorMessage:Please enter a value`;
        }
    }

    creditsErrorMessages: ErrorMessages = {
        required: _ => {
            return $localize`:@@LibraryMakeUpCreditsRequiredErrorMessage:Please enter a valid number of credits`;
        }
    }

    constructor(
        private _makeupCreditManager: MakeupCreditManager
    ) {
        super();
    }

    ngAfterViewInit() {
        this.form.controls.Credits.AddValidators(Validators.max(1000000000));
        this.form.controls.Credits.AddValidators(Validators.min(-999));
        this.form.controls.DefaultLessonLength.AddValidators(Validators.max(1440));
        this.form.controls.DefaultLessonLength.AddValidators(Validators.min(1));
        this.form.controls.LessonPrice.AddValidators(Validators.max(10000000));
        this.form.controls.LessonPrice.AddValidators(Validators.min(0));

        if (!this._billingProfileToEdit) {
            this.SetDefaultValues(null);
        }
    }

    writeFormValue(value: BillingProfileDisplayItem | null): void {
        if (value !== null) {
            this._billingProfileToEdit = value;
        }
        this.SetDefaultValues(value);
    }

    SetDefaultValues(value: BillingProfileDisplayItem | null): void {
        if (!this._categories) {
            return;
        }
        const defaultLessonLength = this.teacher?.PrivilegesAndSettings?.DefaultLessonDuration ?? 30;
        const defaultLessonPrice = this.teacher?.PrivilegesAndSettings?.DefaultLessonPrice ?? 30;
        const defaultCredits = 0;

        let billingMode = value ? value.DefaultBillingMode : (this.teacher?.PrivilegesAndSettings?.DefaultBillingMode ?? BillingProfileDefaultMode.PerLesson);
        if (this._packagesMode && billingMode !== BillingProfileDefaultMode.PerLesson && billingMode !== BillingProfileDefaultMode.Hourly) {
            billingMode = BillingProfileDefaultMode.PerLesson;
        }

        this.form.patchValue({
            EventCategory: value ? this.GetLessonCategory(value.EventCategoryID) : this.GetDefaultLessonCategory(),
            DefaultLessonLength: value ? value.LessonDuration : defaultLessonLength,
            DefaultBillingMode: billingMode,
            LessonPrice: value ? value.BillingRate : defaultLessonPrice,
            Credits: value ? this._makeupCreditManager.GetTotalMakeupCredits(value) : defaultCredits
        });
        this.PatchForm();
        this.BillingModeChanged(this.form.controls.DefaultBillingMode.value);
    }

    AdjustMakeupCredits(creditsChanged = false) {
        if (!this.form.controls.Credits.value || !this.form.controls.DefaultLessonLength.value) {
            this.PatchForm();
            return;
        }

        let makeupMinutes: number;
        if (creditsChanged) {
            makeupMinutes = Math.round(this.form.controls.Credits.value! * this.form.controls.DefaultLessonLength.value!);
        } else {
            makeupMinutes = this.formControl.value!.MakeupMinutes;
        }

        const newMakeupCredits = makeupMinutes / this.GetLessonLengthForMakeupCreditCalculation(this.form.controls.DefaultLessonLength.value);
        const roundedValue = parseFloat(newMakeupCredits.toFixed(2));

        if (roundedValue !== this.form.controls.Credits.value) {
            setTimeout(() => {
                this.form.controls.Credits.patchValue(roundedValue);
                this.PatchForm();
            });
        } else {
            this.PatchForm();
        }
    }

    BillingModeChanged(mode: BillingProfileDefaultMode): void {
        if (mode === BillingProfileDefaultMode.None) {
            this.form.controls.LessonPrice.Disable();
        } else if (!this.formControl?.disabled) {
            this.form.controls.LessonPrice.Enable();
        }
    }

    Changed() {
        this.PatchForm();
    }

    PatchForm() {
        this.formControl?.patchValue({
            Active: false,
            BillingRate: this.form.controls.LessonPrice.value,
            DefaultBillingMode: this.form.controls.DefaultBillingMode.value,
            EventCategoryID: this.form.controls.EventCategory.value?.ID ?? null,
            EventCategory: this.form.controls.EventCategory.value ?? null,
            LessonDuration: this.form.controls.DefaultLessonLength.value!,
            MakeupMinutes: Math.round(this.form.controls.Credits.value! * this.form.controls.DefaultLessonLength.value!),
            ScheduledMakeupMinutes: null,
            StudentID: null,
            TeacherID: null,
            Student: null,
            Teacher: null,
            PackageSummary: null,
        }, { emitModelToViewChange: false });
    }

    UpdateBannerState(value: number | null) {
        this._showBanner = this.makeUpCreditMode === MakeUpCreditMode.Expired && this._billingProfileToEdit !== undefined && this.form.controls.Credits.isDirty && value! > 0;
    }

    private GetLessonLengthForMakeupCreditCalculation(length: number): number {
        return length <= 0 ? 30 : length;
    }

    private GetLessonCategory(id: string | null | undefined): EventCategoryDisplayItem {
        // There will always be a primary category available since there are undeletable categories
        return this._categories.find(c => c.ID === id) ?? this._categories.find(category => category.IsPrimary)!;
    }

    private GetDefaultLessonCategory(): EventCategoryDisplayItem {
        const defaultEventCategoryID = this.teacher?.PrivilegesAndSettings?.DefaultEventCategoryID;
        return this.GetLessonCategory(defaultEventCategoryID);
    }

    private CategoriesAndTeachersReady() {
        if (this._categories && this._teacher) {
            this.SetDefaultValues(this._billingProfileToEdit ?? null);
        }
    }

    private IsCurrentBillingProfileModePackagesCompatible(): boolean {
        return this.form.controls.DefaultBillingMode.value === BillingProfileDefaultMode.Hourly || this.form.controls.DefaultBillingMode.value === BillingProfileDefaultMode.PerLesson;
    }

    get BillingProfileDefaultMode(): typeof BillingProfileDefaultMode {
        return BillingProfileDefaultMode;
    }

    get isPerLesson(): boolean {
        return this.form.controls.DefaultBillingMode.value === BillingProfileDefaultMode.PerLesson;
    }

    get isHourly(): boolean {
        return this.form.controls.DefaultBillingMode.value === BillingProfileDefaultMode.Hourly;
    }

    get isPerMonth(): boolean {
        return this.form.controls.DefaultBillingMode.value === BillingProfileDefaultMode.FixedMonthly;
    }

    get isNone(): boolean {
        return this.form.controls.DefaultBillingMode.value === BillingProfileDefaultMode.None;
    }

    get showCredits(): boolean {
        return this.makeUpCreditMode === MakeUpCreditMode.Allow || this.makeUpCreditMode === MakeUpCreditMode.Expired;
    }

    get showBanner(): boolean {
        return this._showBanner;
    }

    get categories(): EventCategoryDisplayItem[] {
        return this._categories;
    }

    get teacher(): TeacherDisplayItem {
        return this._teacher;
    }

    get packagesMode(): boolean {
        return this._packagesMode;
    }
}
